// Cliente HTTP do aplicativo do cliente.
//
// ⚠️ **Nao usa o `lib/api.ts` da equipe, e isso e' proposital.** Aquele wrapper tem
// um interceptor de 401 que limpa o token e faz `window.location.href = '/login'`
// — a tela de login da EQUIPE. Um cliente cuja sessao vencesse seria despejado no
// login do ERP, olhando um formulario que nao e' dele. Mesmo motivo pelo qual o
// `/catalogo` usa `fetch` cru.
//
// A chave do token tambem e' separada da `fiodecobre.token`: as duas credenciais
// podem coexistir no mesmo navegador (o Ruan abrindo o app pra testar sem
// derrubar a propria sessao do ERP), e misturar as duas chaves faria uma sessao
// apagar a outra.

import type { ModoAviso, StatusCompromisso } from '@fiodecobre/shared';

const TOKEN_KEY = 'fiodecobre.app.token';

export function getTokenApp(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function setTokenApp(token: string | null): void {
  if (token) localStorage.setItem(TOKEN_KEY, token);
  else localStorage.removeItem(TOKEN_KEY);
}

// ---------------------------------------------------------------------------
// ESPELHO — "Acessar como Contato" ([DA-634])
// ---------------------------------------------------------------------------
//
// Enquanto o espelho esta aberto, a credencial DELE fica guardada nesta chave e o
// slot normal carrega a do espelho — assim todo o app (que so conhece uma chave)
// passa a responder como a outra pessoa sem nenhuma tela saber disso.
//
// ⚠️ **A ordem de gravacao importa, e o motivo e' o pior caso**: guardar a propria
// credencial ANTES de trocar. Invertido, um erro no meio deixaria o aparelho com o
// token de espelho e sem o do dono — ele teria que digitar a senha de novo.
const TOKEN_DONO_KEY = 'fiodecobre.app.token.dono';

export function entrarNoEspelho(tokenDoEspelho: string): void {
  const meu = getTokenApp();
  if (meu && !localStorage.getItem(TOKEN_DONO_KEY)) localStorage.setItem(TOKEN_DONO_KEY, meu);
  setTokenApp(tokenDoEspelho);
}

/**
 * Volta a ser voce. `false` = nao havia credencial guardada (aba limpa, aparelho
 * trocado) — a tela entao encerra a sessao, que e' o unico desfecho honesto.
 */
export function sairDoEspelho(): boolean {
  const meu = localStorage.getItem(TOKEN_DONO_KEY);
  localStorage.removeItem(TOKEN_DONO_KEY);
  if (!meu) { setTokenApp(null); return false; }
  setTokenApp(meu);
  return true;
}

/** Ha um espelho aberto neste aparelho? (o que decide a TELA e' o `cliente.espelho`
 *  que o servidor manda; isto serve pra limpeza, ver `AppCliente`). */
export function temEspelhoAberto(): boolean {
  return localStorage.getItem(TOKEN_DONO_KEY) !== null;
}

// O ultimo celular que entrou NESTE aparelho, ja no formato da tela.
//
// ⛔ **So o telefone. A senha nunca e' guardada** — nem cifrada, nem "escondida".
// Quem tem que lembrar senha e' o cofre do proprio celular (o iPhone oferece
// "Salvar senha" ao entrar, e depois preenche sozinho); o formulario ja declara
// os campos do jeito que esse cofre entende. Guardar senha por conta propria
// significaria po-la em texto no aparelho, e um celular emprestado abriria a
// conversa de outra pessoa com a loja.
const TELEFONE_KEY = 'fiodecobre.app.telefone';

export function getTelefoneLembrado(): string {
  return localStorage.getItem(TELEFONE_KEY) ?? '';
}

export function setTelefoneLembrado(mascarado: string): void {
  if (mascarado) localStorage.setItem(TELEFONE_KEY, mascarado);
}

export class ErroApp extends Error {
  status: number;
  corpo: Record<string, unknown>;
  constructor(status: number, mensagem: string, corpo: Record<string, unknown> = {}) {
    super(mensagem);
    this.status = status;
    this.corpo = corpo;
  }
}

async function request<T>(metodo: string, caminho: string, corpo?: unknown): Promise<T> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  const token = getTokenApp();
  if (token) headers.Authorization = `Bearer ${token}`;

  const resp = await fetch(`/api/app${caminho}`, {
    method: metodo,
    headers,
    body: corpo === undefined ? undefined : JSON.stringify(corpo),
  });

  const texto = await resp.text();
  const dados = texto ? (JSON.parse(texto) as Record<string, unknown>) : {};

  if (!resp.ok) {
    // 401 apaga a credencial local, mas NAO redireciona: quem decide o que
    // mostrar e' a tela raiz do app, que volta pro cadastro/entrada dela mesma.
    if (resp.status === 401) setTokenApp(null);
    throw new ErroApp(resp.status, String(dados.erro ?? 'Nao deu certo. Tente de novo.'), dados);
  }
  return dados as T;
}

/** Papel DENTRO do aplicativo — nada a ver com o perfil da pessoa no ERP. */
export type PapelApp = 'usuario' | 'administrador';

export interface ClienteApp {
  id: number;
  nome: string;
  telefone: string;
  email: string | null;
  tem_ficha: boolean;
  papel: PapelApp;
  /** A marca do prestador (migration 0237). Vazio = assina com o nome da pessoa. */
  fantasia: string | null;
  logo_url: string | null;
  /** Excecao da trava da marca da loja (migration 0252). So serve pra tela NAO
   *  avisar quem esta liberado — quem recusa de verdade e' o servidor. */
  marca_loja_liberada: boolean;
  /**
   * "Acessar como Contato" ([DA-634]): esta sessao e' um ESPELHO — o admin do app
   * olhando a tela de outra pessoa, em leitura.
   *
   * ⛔ **Isto nao tranca nada, so DESENHA.** Quem recusa cada gravacao e' o
   * servidor, em toda requisicao; a tela usa este campo pra faixa fixa do topo e
   * pra nao oferecer o que vai ser recusado ([DA-593]: lista que oferece o que o
   * backend nega faz o operador errar fazendo o que a tela mandou).
   */
  espelho?: boolean;
}

/** Uma conta do app na lista de "Acessar como Contato" ([DA-634]). */
export interface ContaEspelho {
  id: number;
  nome: string;
  telefone: string;
  papel: PapelApp;
  ativo: boolean;
  tem_ficha: boolean;
  /** Sem conversa, o chat abre vazio no espelho — e isso nao e' defeito. */
  tem_conversa: boolean;
}

export interface SessaoApp {
  token: string;
  cliente: ClienteApp;
}

/**
 * O que a HOME mostra embaixo de cada opcao do menu ([DA-575]).
 *
 * ⚠️ `titulos_abertos` e `notas_total` sao `null`, nunca 0, pra quem nao tem ficha na
 * loja: zero diria "voce nao deve nada" — uma frase sobre a conta dele — quando o
 * fato e' que nao ha conta pra consultar. Sao dois textos diferentes na tela.
 */
export interface ResumoApp {
  nao_lidas: number;
  /** `null` no ESPELHO ([DA-634]): area privada nao conta nem se anuncia. */
  orcamentos: number | null;
  /** Compromissos de hoje em diante — o mesmo corte da lista da agenda.
   *  `null` no espelho, pelo mesmo motivo do irmao acima. */
  agenda: number | null;
  /** O proximo compromisso, pra linha da home dizer a HORA em vez de so contar. */
  agenda_proximo: string | null;
  agenda_proximo_cliente: string | null;
  titulos_abertos: number | null;
  /** Quantas notas o cliente tem AO TODO — a 2a metade da linha "Financeiro".
   *  ⚠️ E' o acervo inteiro (o que a aba "Notas Fiscais" mostra), nao as notas das
   *  contas em aberto: com a area tendo duas abas, e' este o numero que a linha
   *  precisa anunciar. */
  notas_total: number | null;
  /** Quantos boletos ele tem pra pagar — a 3a metade da linha "Financeiro" ([DA-629]).
   *  ⚠️ Vai na FRENTE das outras duas quando existe: e' o unico item da area que pede
   *  acao com prazo, e o push pode nunca ter chegado (ha cliente com o aviso
   *  desligado). `null` = sem ficha na loja, mesma regra dos irmaos acima. */
  boletos: number | null;
}

/** Um compromisso da agenda do prestador (migration 0239). */
export interface CompromissoApp {
  id: number;
  /** Instante com fuso, vindo do banco. A tela formata em America/Sao_Paulo. */
  quando: string;
  /** O cliente DELE — texto livre, nunca ficha nossa. Continua existindo mesmo
   *  quando `cliente_id` aponta pro cadastro (migration 0261) — ver o comentario
   *  daquela coluna: e' um ponteiro que CONVIVE com o texto, nao o substitui. */
  cliente_final: string | null;
  /** O cliente do CADASTRO (migration 0261), quando escolhido pelo combo. `null` =
   *  compromisso com so o texto livre acima — o caso comum de antes desta onda. */
  cliente_id: number | null;
  /** So vem preenchido quando `cliente_id` aponta pra um cliente COM telefone — e'
   *  o que liga o botão de WhatsApp na folha. */
  cliente_telefone: string | null;
  categoria_id: number | null;
  orcamento_id: number | null;
  /** Pronto do servidor: "DONA MARIA" ou "Orçamento #12". */
  orcamento_rotulo: string | null;
  orcamento_itens: number | null;
  observacoes: string | null;
  aviso: ModoAviso;
  aviso_horas: number | null;
  /** Quando o aviso sai. `null` = nao ha aviso (ver `descreverAviso` no shared). */
  avisar_em: string | null;
  /** Carimbo do disparo — a tela mostra "avisado" em vez de prometer o que ja saiu. */
  avisado_em: string | null;
  /**
   * O que uma PESSOA decidiu (migration 0240).
   *
   * ⚠️ "Vencido" e "falta uma hora" NAO estao aqui de proposito: sao funcao do
   * relogio e a tela calcula sozinha (`estadoDoCompromisso`, no shared). Se
   * viessem prontos do servidor, a cor congelaria no instante do fetch.
   */
  status: StatusCompromisso;
  /** Quando ele decidiu. `null` enquanto o compromisso esta so marcado. */
  status_em: string | null;
}

export interface CorpoCompromisso {
  /** `YYYY-MM-DD` no relogio da loja. */
  data: string;
  /** `HH:MM` no relogio da loja. */
  hora: string;
  cliente_final?: string | null;
  cliente_id?: number | null;
  categoria_id?: number | null;
  orcamento_id?: number | null;
  observacoes?: string | null;
  aviso?: ModoAviso;
  aviso_horas?: number | null;
}

/** Uma categoria da agenda (migration 0262) — lista fixa da casa, nao por prestador. */
export interface CategoriaAgendaApp {
  id: number;
  nome: string;
}

// ---------------------------------------------------------------------------
// Cadastro de CLIENTES DO PRESTADOR (migration 0260)
// ---------------------------------------------------------------------------

/** Um cliente do prestador — os clientes DELE, nunca ficha nossa (mesmo sigilo dos
 *  Orçamentos e da Agenda: só o próprio prestador lê a lista). */
export interface ClienteFinalApp {
  id: number;
  nome: string;
  /** Digitos com DDI 55, ou `null` — ele nao informou. */
  telefone: string | null;
  endereco: string | null;
  observacoes: string | null;
  criado_em: string;
  atualizado_em: string;
}

export interface CorpoClienteFinal {
  nome: string;
  telefone?: string | null;
  endereco?: string | null;
  observacoes?: string | null;
}

/** Uma entrada por pessoa que reagiu. `fromMe` significa "da loja" — o formato e'
 *  o mesmo das caixas de WhatsApp, porque a reacao mora no `metadata` da mensagem. */
export interface ReacaoApp {
  emoji: string;
  jid: string;
  name: string;
  fromMe: boolean;
  at: string;
}

export interface MensagemApp {
  id: number;
  direcao: 'inbound' | 'outbound';
  tipo: string;
  texto: string | null;
  midia_url: string | null;
  midia_mime: string | null;
  /** So em `tipo: 'document'` — e' o nome que a vendedora anexou, e o rotulo do balao ([DA-572]). */
  midia_nome?: string | null;
  midia_bytes?: number | null;
  criado_em: string;
  reactions?: ReacaoApp[];
  /** So nas mensagens do cliente: a loja ja abriu a conversa depois desta (DA-538). */
  lida_loja?: boolean;
  /** A loja apagou para todos (migration 0223). O conteudo ja chega vazio. */
  apagada?: boolean;
}

/** O que o link de 1o acesso entrega pra tela. O telefone vem MASCARADO — o link
 *  pode ter sido encaminhado, e a pagina nao entrega o numero inteiro de ninguem. */
export interface ConviteApp {
  nome: string | null;
  telefone_mascarado: string;
}

export const appApi = {
  lerConvite: (token: string) => request<ConviteApp>('GET', `/convite/${encodeURIComponent(token)}`),
  // ⛔ `convite` e' OBRIGATORIO (16/08/2026): so se entra no AppCel por convite da
  // loja, e o telefone vem do token — nunca de campo digitado. O servidor recusa
  // com 403 se faltar.
  cadastrar: (dados: { nome: string; email?: string; senha: string; convite: string }) =>
    request<SessaoApp>('POST', '/cadastro', dados),
  entrar: (dados: { telefone: string; senha: string }) =>
    request<SessaoApp>('POST', '/login', dados),

  // "Esqueci minha senha" (02/09/2026) — so funciona depois que a LOJA libera pelo
  // botao Resetar senha, e a liberacao vale 2h. ⚠️ A pergunta vem antes dos campos
  // de senha de proposito: recusar depois da pessoa preencher tudo e' o defeito da
  // [DA-560]. `liberado: false` e' a resposta pra numero sem conta, conta sem reset
  // e janela vencida — as tres iguais.
  redefinicaoLiberada: (telefone: string) =>
    request<{ liberado: boolean; nome: string | null }>('POST', '/redefinir/inicio', { telefone }),
  // Devolve a sessao pronta: quem acabou de criar a senha entra direto, sem digitar
  // duas vezes a mesma coisa.
  redefinir: (telefone: string, senha: string) =>
    request<SessaoApp>('POST', '/redefinir', { telefone, senha }),
  // Devolve a sessao inteira, nao so o cliente: o token vem RENOVADO a cada
  // abertura, pra quem usa o app nao ser deslogado ao completar 30 dias.
  eu: () => request<SessaoApp>('GET', '/me'),

  // O estado de cada linha da home, numa chamada so — ver o comentario da rota.
  resumo: () => request<ResumoApp>('GET', '/resumo'),

  // "Acessar como Contato" ([DA-634]) — so o administrador do app enxerga isto.
  espelhoContas: () => request<ContaEspelho[]>('GET', '/espelho/contas'),
  espelhar: (id: number) => request<SessaoApp>('POST', `/espelho/${id}`),

  // A MARCA do prestador — o que assina o PDF do orcamento (migration 0237).
  // As tres devolvem a sessao inteira: o `cliente` volta atualizado e a tela nao
  // precisa remontar o objeto na mao (mesma licao da [DA-463]).
  salvarFantasia: (fantasia: string) => request<SessaoApp>('PUT', '/perfil', { fantasia }),
  enviarLogo: (base64: string, mime: string) =>
    request<SessaoApp>('POST', '/perfil/logo', { base64, mime }),
  removerLogo: () => request<SessaoApp>('DELETE', '/perfil/logo'),
  // Trocar a propria senha (02/09/2026) — o par do "Resetar senha" que a loja tem
  // em Configuracoes → AppCel. ⚠️ Devolve a sessao com o TOKEN NOVO, e nao por
  // simetria: a troca incrementa a `token_versao`, o que mata o token que esta
  // guardado neste aparelho. Quem chama GRAVA o que volta (`setTokenApp`), senao a
  // pessoa troca a senha e cai na tela de entrada no mesmo instante.
  trocarSenha: (senha_atual: string, senha_nova: string) =>
    request<SessaoApp>('PUT', '/senha', { senha_atual, senha_nova }),
  // `visivel` diz ao servidor se a tela esta MESMO na frente da pessoa. So entao a
  // leitura e' marcada — a consulta continua rodando com o app no bolso, e marcar
  // ali zeraria o numero do icone de um app que ninguem esta olhando.
  mensagens: (depois?: number | null, limite?: number, visivel?: boolean) => {
    const p = new URLSearchParams();
    if (depois) p.set('after', String(depois));
    if (limite) p.set('limit', String(limite));
    if (visivel) p.set('visivel', '1');
    const q = p.toString();
    return request<{ conversa_id: number; mensagens: MensagemApp[] }>('GET', `/mensagens${q ? `?${q}` : ''}`);
  },
  enviarTexto: (texto: string) => request<MensagemApp>('POST', '/mensagens', { texto }),
  enviarAudio: (base64: string, mime: string) =>
    request<MensagemApp>('POST', '/mensagens/audio', { base64, mime }),
  enviarFoto: (base64: string, mime: string) =>
    request<MensagemApp>('POST', '/mensagens/foto', { base64, mime }),
  // Apagar em lote. Devolve o que saiu e o que foi recusado — ver o comentario da
  // rota: lote parcial e' informacao, nao erro.
  //
  // ⚠️ `paraTodos` PROPOE, nao decide: o servidor reconfere com a mesma regra
  // (`decidirApagarLote`) e recusa em vez de rebaixar pra "so pra mim" calado
  // ([DA-574]). Sem ele, o desfecho e' esconder so desta tela.
  apagarMensagens: (ids: number[], paraTodos = false) =>
    request<{ apagadas: number[]; recusadas: number }>('POST', '/mensagens/apagar', { ids, paraTodos }),
  reagir: (mensagemId: number, emoji: string | null) =>
    request<{ id: number; reactions: ReacaoApp[] }>('POST', `/mensagens/${mensagemId}/reagir`, { emoji }),

  // "Financeiro" — as tres abas. ⚠️ Nao vai `cliente_id` nenhum em nenhuma das
  // tres: quem decide de quem e' o dado e' o servidor, lendo a ficha amarrada ao
  // token. Mandar o id daqui seria oferecer o furo.
  financeiro: () => request<FinanceiroApp>('GET', '/financeiro'),
  // Aba "Notas Fiscais": o acervo, incluindo compra ja paga (26/08/2026).
  // `mes` (`YYYY-MM`) filtra a lista; sem ele vem "Todas", o padrao. A lista de
  // meses disponiveis vem junto nas duas formas.
  notasFiscais: (mes?: string | null) =>
    request<NotasFiscaisApp>('GET', `/financeiro/notas${mes ? `?mes=${encodeURIComponent(mes)}` : ''}`),
  // Aba "Boletos": o papel que a loja subiu pra ele pagar (27/08/2026).
  boletos: () => request<BoletosApp>('GET', '/financeiro/boletos'),

  // "Meus Orcamentos" — o documento que o PRESTADOR entrega ao cliente DELE.
  // 🔒 Nenhuma tela do ERP le isto; a unica porta e' esta, e ela so abre com token
  // de cliente. Ver o cabecalho de `orcamentos.routes.ts` no backend.
  orcamentos: (modelos = false) =>
    request<ResumoOrcamentoApp[]>('GET', `/orcamentos?modelos=${modelos ? '1' : '0'}`),
  orcamento: (id: number) => request<OrcamentoApp>('GET', `/orcamentos/${id}`),
  criarOrcamento: (corpo: { cliente_final?: string | null }) =>
    request<OrcamentoApp>('POST', '/orcamentos', { itens: [], ...corpo }),
  salvarOrcamento: (id: number, corpo: CorpoSalvarOrcamento) =>
    request<{ id: number; itens: ItemOrcamentoApp[]; totais: TotaisOrcamento }>('PUT', `/orcamentos/${id}`, corpo),
  duplicarOrcamento: (id: number, corpo: { modelo?: boolean; nome_modelo?: string; cliente_final?: string }) =>
    request<{ id: number; modelo: boolean }>('POST', `/orcamentos/${id}/duplicar`, corpo),
  excluirOrcamento: (id: number) => request<{ ok: boolean }>('DELETE', `/orcamentos/${id}`),
  buscarMaterial: (q: string) =>
    request<ProdutoBusca[]>('GET', `/orcamentos/catalogo/buscar?q=${encodeURIComponent(q)}`),

  // AGENDA — os compromissos de trabalho DELE (migration 0239).
  // ⚠️ Data e hora viajam SEPARADAS, como relogio de parede da loja, e o instante e'
  // montado no servidor. Mandar um ISO daqui gravaria o compromisso no fuso do
  // aparelho — e o erro so apareceria no dia, com o aviso saindo na hora errada.
  /**
   * A GRADE: os compromissos de UM dia (`YYYY-MM-DD` no relogio da loja).
   *
   * ⚠️ A leitura de LISTA (`?anteriores=1`) continua existindo no servidor, mas
   * ficou sem tela em 19/08/2026 — a grade do dia substituiu as abas
   * Proximos/Anteriores por decisao dele. Nao foi apagada la porque e' a mesma rota;
   * daqui saiu pra nao parecer viva.
   */
  agendaDoDia: (dia: string) => request<CompromissoApp[]>('GET', `/agenda?dia=${dia}`),
  /**
   * A aba SEMANA: uma janela de `dias` (default 7) a partir de `desde`. Mesma
   * regra de fuso do `agendaDoDia` — o corte roda no banco, nunca em JS.
   */
  agendaDaSemana: (desde: string, dias = 7) =>
    request<CompromissoApp[]>('GET', `/agenda?desde=${desde}&dias=${dias}`),
  /** A lista fixa de categorias (migration 0262) — carregada uma vez, mapeada
   *  localmente por id; nao muda por prestador. */
  agendaCategorias: () => request<CategoriaAgendaApp[]>('GET', '/agenda/categorias'),
  marcarStatus: (id: number, status: StatusCompromisso) =>
    request<CompromissoApp>('PATCH', `/agenda/${id}/status`, { status }),
  criarCompromisso: (corpo: CorpoCompromisso) => request<CompromissoApp>('POST', '/agenda', corpo),
  salvarCompromisso: (id: number, corpo: CorpoCompromisso) =>
    request<CompromissoApp>('PUT', `/agenda/${id}`, corpo),
  excluirCompromisso: (id: number) => request<{ ok: boolean }>('DELETE', `/agenda/${id}`),

  // CADASTRO DE CLIENTES do prestador (migration 0260) — os clientes DELE.
  clientesFinais: () => request<ClienteFinalApp[]>('GET', '/clientes'),
  clienteFinal: (id: number) => request<ClienteFinalApp>('GET', `/clientes/${id}`),
  criarClienteFinal: (corpo: CorpoClienteFinal) => request<ClienteFinalApp>('POST', '/clientes', corpo),
  salvarClienteFinal: (id: number, corpo: CorpoClienteFinal) =>
    request<ClienteFinalApp>('PUT', `/clientes/${id}`, corpo),
  excluirClienteFinal: (id: number) => request<{ ok: boolean }>('DELETE', `/clientes/${id}`),

  // Notificacao (DA-532). A chave publica vem do servidor em vez de embutida no
  // bundle: girar o par VAPID vira mexer no `.env`, sem republicar o frontend.
  pushEstado: () => request<EstadoPush>('GET', '/push/estado'),
  pushInscrever: (ins: { endpoint: string; p256dh: string; auth: string }) =>
    request<{ ok: boolean; aparelhos: number }>('POST', '/push/inscrever', ins),
  pushCancelar: (endpoint: string) =>
    request<{ ok: boolean; aparelhos: number }>('POST', '/push/cancelar', { endpoint }),
};

/** A nota de um titulo — so o que o botao de compartilhar precisa saber. */
export interface NotaApp {
  tipo: 'nfe' | 'nfce';
  id: number;
  numero: number;
}

/** Um titulo em aberto do cliente. `vencido`/`dias_atraso` vem calculado do
 *  servidor — a data do celular pode estar errada, e a do banco nao. */
export interface TituloApp {
  id: number;
  orcamento_numero: string | null;
  data_emissao: string | null;
  data_vencimento: string | null;
  valor: number;
  dias_atraso: number;
  vencido: boolean;
  /** ⚠️ `null` e' informacao, nao dado faltando: ha titulo pendente sem nota
   *  emitida (medido: 9 dos 54 em 20/08/2026). A linha reserva o espaco do botao
   *  assim mesmo, senao a coluna de valores desalinha. */
  nota: NotaApp | null;
}

export interface FinanceiroApp {
  titulos: TituloApp[];
  total_aberto: number;
  vencidos: number;
  /** Quantas notas o botao "Baixar Notas" vai entregar (10/09/2026).
   *  ⛔ **Nao e' `titulos.length`**: pedido parcelado poe varios titulos em cima da
   *  MESMA nota, e o pacote leva um arquivo por nota. Contado no servidor, pela mesma
   *  consulta que monta o zip — o rotulo e o arquivo nao podem divergir. */
  notas_no_pacote: number;
}

/** Uma nota do ACERVO (aba "Notas Fiscais"). Mais campos que a `NotaApp` porque
 *  aqui ela e' a propria linha, e nao um botao pendurado num titulo. */
export interface NotaAcervoApp {
  tipo: 'nfe' | 'nfce';
  id: number;
  numero: number;
  emitido_em: string | null;
  valor: number;
}

/** Um mes que o cliente TEM nota (01/09/2026).
 *
 *  ⚠️ **UM numero so desde 10/09/2026.** Ate a vespera eram dois — `total` pra LISTA
 *  (NF-e + cupom) e `nfe` pro PACOTE, porque o cupom ficava de fora do zip; na
 *  ILUMINAR de agosto, 34 e 33. Com o cupom ENTRANDO no pacote os dois passaram a dar
 *  sempre o mesmo valor, e o `nfe` saiu: campo que so pode repetir o vizinho nao mede
 *  nada. */
export interface MesDeNotasApp {
  /** `YYYY-MM`, ja no fuso da loja — a tela so formata. */
  mes: string;
  total: number;
}

export interface NotasFiscaisApp {
  /** As `limite` mais recentes (do mes escolhido, quando ha um). */
  notas: NotaAcervoApp[];
  /** ⚠️ Quantas existem AO TODO — e a tela DIZ, quando for maior que o que veio.
   *  Entregar 50 de 128 calado faria as 78 antigas sumirem sem aviso. */
  total: number;
  limite: number;
  /** O mes que o servidor de fato aplicou. `null` = "Todas". ⚠️ Le-se ELE, nao o que
   *  a tela pediu: mes torto vira "Todas" no servidor, e a pastilha tem que
   *  acompanhar em vez de ficar marcando um filtro que nao esta valendo. */
  mes: string | null;
  /** ⛔ **A lista de meses vem do SERVIDOR, sempre inteira.** Derivar as pastilhas
   *  das notas que chegaram desenharia so os meses das 50 que couberam — e depois de
   *  filtrar, sobraria uma pastilha so. */
  meses: MesDeNotasApp[];
}

/** O caminho do PDF de uma nota. Nao passa pelo `request` porque a resposta e' um
 *  arquivo, nao JSON — o token vai no cabecalho, entao o download e' feito por
 *  `fetch` + blob, e nunca por `<a href>` (que iria sem credencial). */
export function caminhoPdfNota(nota: Pick<NotaApp, 'tipo' | 'id'>): string {
  return `/api/app/financeiro/nota/${nota.tipo}/${nota.id}/pdf`;
}

/** O caminho do .zip com as notas de um mes (01/09/2026). Mesma regra do PDF: sai por
 *  `fetch` com o token no cabecalho, nunca por `<a href>`. */
export function caminhoPacoteNotas(mes: string): string {
  return `/api/app/financeiro/notas/pacote?mes=${encodeURIComponent(mes)}`;
}

/** O caminho do .zip com as notas do que ele DEVE (10/09/2026), pra aba Contas a
 *  Pagar. ⚠️ Sem parametro nenhum: o recorte e' "em aberto" e o cliente sai do token
 *  — a tela nao escolhe conjunto, so' aperta o botao. */
export function caminhoPacoteEmAberto(): string {
  return '/api/app/financeiro/notas/pacote-em-aberto';
}

/** Um boleto que a loja subiu pro cliente pagar (aba "Boletos", 27/08/2026).
 *
 *  ⚠️ **Valor e vencimento sao do TITULO, nao do arquivo** — nao ha copia guardada
 *  na linha do boleto, entao renegociacao aparece aqui no mesmo instante. E o boleto
 *  some da lista sozinho quando a loja baixa o titulo. */
export interface NotaDoBoletoApp {
  tipo: 'nfe' | 'nfce';
  numero: number;
}

export interface BoletoApp {
  id: number;
  /** Soma dos titulos cobertos — o numero impresso no papel. ⚠️ NAO encolhe quando a
   *  loja baixa um deles: o boleto que o cliente tem na mao nao muda. */
  valor: number;
  data_vencimento: string | null;
  dias_atraso: number;
  vencido: boolean;
  /** So os digitos. `null` quando a loja subiu o PDF sem colar a linha. */
  linha_digitavel: string | null;
  /** PDF e' opcional desde 20/10/2026 — sem ele, nada de botao de baixar. */
  tem_pdf: boolean;
  enviado_em: string | null;
  /** Quantos titulos o boleto cobre. A tela so explica quando for mais de um. */
  titulos: number;
  /** As notas cobertas — o numero que o cliente lanca no contas a pagar DELE ([DA-630]).
   *  ⚠️ Pode vir MENOR que `titulos`: ha titulo sem nota, e a tela nao inventa. */
  notas: NotaDoBoletoApp[];
}

export interface BoletosApp {
  boletos: BoletoApp[];
  /** A loja — o cliente confere no banco que paga pra ela. */
  recebedor: { nome: string; cnpj: string };
  pagador: { nome: string; documento: string | null } | null;
}

/** O caminho do PDF de um boleto. Mesma regra da nota: sai por `fetch` com o token,
 *  nunca por `<a href>` — o arquivo mora fora da pasta publica de proposito. */
export function caminhoPdfBoleto(id: number): string {
  return `/api/app/financeiro/boleto/${id}/pdf`;
}

// ---------------------------------------------------------------------------
// "Meus Orcamentos" — o documento do prestador
// ---------------------------------------------------------------------------

/** Uma linha do documento. Material aponta pro catalogo; servico e' texto livre. */
export interface ItemOrcamentoApp {
  id: number;
  tipo: 'material' | 'servico';
  produto_id: number | null;
  descricao: string;
  quantidade: number;
  /** O que ELE digitou. `null` = sem preco proprio, vale a referencia do catalogo. */
  valor_unitario: number | null;
  /** Quanto custa HOJE no catalogo da loja. Referencia — nao e' o valor do papel. */
  preco_ref: number | null;
  /** Foto de hoje no catalogo. Viva como o `preco_ref`: nao e' copia guardada na linha. */
  foto_url: string | null;
  ordem: number;
}

export interface TotaisOrcamento {
  material: number;
  servico: number;
  total: number;
}

export interface OrcamentoApp {
  id: number;
  cliente_final: string | null;
  observacoes: string | null;
  modelo: boolean;
  nome_modelo: string | null;
  criado_em: string;
  atualizado_em: string;
  itens: ItemOrcamentoApp[];
  totais: TotaisOrcamento;
  /** O nome que vai no TOPO do papel — o documento e' dele. */
  prestador: string;
  /** A marca: quando ha `fantasia`, ela assina no lugar do nome da pessoa. */
  fantasia: string | null;
  logo_url: string | null;
}

export interface ResumoOrcamentoApp {
  id: number;
  cliente_final: string | null;
  nome_modelo: string | null;
  modelo: boolean;
  atualizado_em: string;
  itens: number;
  total: number;
}

export interface CorpoSalvarOrcamento {
  cliente_final: string | null;
  observacoes: string | null;
  nome_modelo: string | null;
  itens: Array<{
    tipo: 'material' | 'servico';
    produto_id: number | null;
    descricao: string;
    quantidade: number;
    valor_unitario: number | null;
  }>;
}

export interface ProdutoBusca {
  id: number;
  codigo_interno: string | null;
  descricao: string;
  preco: number;
  /** Miniatura na busca. 1.501 dos 1.503 ativos tem foto; os dois sem viram moldura vazia. */
  foto_url: string | null;
  unidade: string | null;
}

export interface EstadoPush {
  /** O servidor tem chave configurada. Falso = nao adianta oferecer o botao. */
  disponivel: boolean;
  chave_publica: string;
  /** Quantos aparelhos DESTE cliente estao com aviso ligado. */
  aparelhos: number;
}
